import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, ShoppingBag, Heart, CreditCard, Truck, ShieldCheck, ChevronLeft, ChevronRight, AlertCircle, Clock, Calendar, Info } from 'lucide-react';
import { Product } from '../types';

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onAddToCart: (product: Product, quantity: number) => Promise<boolean> | void;
  onToggleWishlist: (product: Product) => void;
  isInWishlist: (id: number) => boolean;
  onNavigate: (page: string) => void;
}

// More reliable placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/600x800/f5f5f5/999999?text=No+Image';

// Helper function to repair base64 images
const repairBase64Image = (imageUrl: string | undefined): string => {
  if (!imageUrl || typeof imageUrl !== 'string') return PLACEHOLDER_IMAGE;
  
  const trimmed = imageUrl.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;
  
  // Fix malformed base64 (missing semicolon or comma)
  if (trimmed.startsWith('data:image/')) {
    let fixed = trimmed;
    
    // Fix missing semicolon before base64
    if (fixed.includes(':base64') && !fixed.includes(';base64')) {
      fixed = fixed.replace(':base64', ';base64,');
    }
    
    // Fix missing comma after base64
    if (fixed.includes(';base64') && !fixed.includes(';base64,')) {
      fixed = fixed.replace(';base64', ';base64,');
    }
    
    return fixed;
  }
  
  // Keep blob URLs
  if (trimmed.startsWith('blob:')) {
    return trimmed;
  }
  
  // Keep absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  
  return PLACEHOLDER_IMAGE;
};

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ 
  product, 
  onClose, 
  onAddToCart, 
  onToggleWishlist,
  isInWishlist,
  onNavigate
}) => {
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const [mainImageError, setMainImageError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAddToCartProcessing, setIsAddToCartProcessing] = useState(false);
  const [isCheckoutProcessing, setIsCheckoutProcessing] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);

  // Check for promo
  const extendedProduct = product as any;
  const isPromo = 
    extendedProduct.is_promo === true || 
    extendedProduct.is_promo === 1 || 
    extendedProduct.is_promo === '1' ||
    (extendedProduct.promo_price && extendedProduct.promo_price > 0);
  
  const promoPrice = extendedProduct.promo_price;
  const promoPercentage = extendedProduct.promo_percentage;
  const promoLabel = extendedProduct.promo_label || 'SALE';
  const promoEndDate = extendedProduct.promo_end_date;
  
  // Check if promo is still active
  const isPromoActive = !promoEndDate || new Date(promoEndDate) >= new Date();
  const showPromo = isPromo && isPromoActive && promoPrice;

  // Calculate display prices
  const displayPrice = showPromo ? promoPrice : product.price || 0;
  const originalPrice = showPromo ? product.price : null;
  const savings = showPromo && product.price && promoPrice ? product.price - promoPrice : 0;
  const savingsPercentage = promoPercentage || (showPromo && product.price && promoPrice 
    ? Math.round(((product.price - promoPrice) / product.price) * 100) 
    : 0);

  // Check if product is pre-order
  const isPreOrder = extendedProduct.isPreOrder === true;
  const preOrderReleaseDate = extendedProduct.preOrderReleaseDate;

  // Check stock
  const stock = product.stock || 0;
  const isOutOfStock = stock <= 0;

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return null;
    }
  };

  // Format release date
  const formatReleaseDate = (dateString: string | null) => {
    if (!dateString) return 'Coming Soon';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Coming Soon';
    }
  };

  // Create array of all available images
  const allImages: string[] = [];
  
  // Add main image if it exists and is valid
  if (product.image && typeof product.image === 'string' && product.image.length > 5) {
    allImages.push(repairBase64Image(product.image));
  }
  
  // Add gallery images if they exist
  if (product.galleryImages && Array.isArray(product.galleryImages)) {
    const validGalleryImages = product.galleryImages.filter(
      img => img && typeof img === 'string' && img.length > 5
    );
    allImages.push(...validGalleryImages.map(img => repairBase64Image(img)));
  }

  // Remove duplicates
  const uniqueImages = [...new Set(allImages)];

  useEffect(() => {
    setSelectedImage(0);
    setDetailQuantity(1);
    setStockError(null);
    document.body.style.overflow = 'hidden';
    return () => { 
      document.body.style.overflow = 'unset';
      setIsAddToCartProcessing(false);
      setIsCheckoutProcessing(false);
      setStockError(null);
    };
  }, [product]);

  // Get current display image with fallback
  const getCurrentImage = () => {
    if (mainImageError || !uniqueImages.length || imageErrors[selectedImage]) {
      return PLACEHOLDER_IMAGE;
    }
    return uniqueImages[selectedImage];
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const handleMainImageError = () => {
    setMainImageError(true);
  };

  // Handle Add to Cart only with stock check
  const handleAddToCart = async () => {
    if (isAddToCartProcessing || isCheckoutProcessing) return;
    
    if (isOutOfStock) {
      setStockError('Sorry, this item is out of stock');
      return;
    }
    
    if (stock < detailQuantity) {
      setStockError(`Sorry, only ${stock} item(s) available in stock`);
      return;
    }
    
    setIsAddToCartProcessing(true);
    setStockError(null);
    
    try {
      const result = await Promise.resolve(onAddToCart(product, detailQuantity));
      
      if (result === false) {
        setStockError('Failed to add item to cart. Please try again.');
      } else {
        const toast = document.createElement('div');
        toast.className = 'fixed top-24 right-4 bg-green-600 text-white px-6 py-3 rounded-sm shadow-lg z-[300] animate-slide-in';
        toast.textContent = 'Added to cart successfully';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setStockError('Failed to add item to cart. Please try again.');
    } finally {
      setIsAddToCartProcessing(false);
    }
  };

  // Handle Checkout with session storage for page reload recovery
  const handleCheckout = () => {
    if (isCheckoutProcessing || isAddToCartProcessing) {
      return;
    }
    
    if (isOutOfStock) {
      setStockError('Sorry, this item is out of stock and cannot be purchased');
      return;
    }
    
    if (stock < detailQuantity) {
      setStockError(`Sorry, only ${stock} item(s) available in stock. Please adjust quantity.`);
      return;
    }
    
    const checkoutData = {
      product: {
        id: product.id,
        name: product.name,
        price: product.price,
        image: repairBase64Image(product.image),
        category: product.category,
        description: product.description,
        condition: product.condition,
        weight: product.weight,
        karat: product.karat,
        goldType: product.goldType,
        stock: product.stock,
        isPreOrder: extendedProduct.isPreOrder,
        preOrderReleaseDate: extendedProduct.preOrderReleaseDate,
        is_promo: extendedProduct.is_promo,
        promo_price: extendedProduct.promo_price,
        promo_percentage: extendedProduct.promo_percentage,
        promo_label: extendedProduct.promo_label
      },
      quantity: detailQuantity,
      timestamp: Date.now()
    };
    
    sessionStorage.setItem('directCheckout', JSON.stringify(checkoutData));
    onClose();
    onNavigate('checkout');
  };

  const nextImage = () => {
    setSelectedImage(prev => Math.min(uniqueImages.length - 1, prev + 1));
  };

  const prevImage = () => {
    setSelectedImage(prev => Math.max(0, prev - 1));
  };

  // Get condition display information
  const getConditionInfo = () => {
    const condition = product.condition || 'Brand New';
    
    switch(condition) {
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return {
          label: 'Pre-loved',
          badgeColor: 'bg-amber-100 text-amber-800 border-amber-200',
          description: 'This item is pre-owned and has been carefully inspected.',
          detailedInfo: 'Authenticity guaranteed, carefully inspected, like brand new'
        };
      case 'Vintage':
        return {
          label: 'Vintage',
          badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
          description: 'A vintage piece with character and history.',
          detailedInfo: 'Authentic vintage, character with age, one-of-a-kind'
        };
      case 'Like New':
        return {
          label: 'Like New',
          badgeColor: 'bg-blue-100 text-blue-800 border-blue-200',
          description: 'Minimal to no signs of wear. Looks almost new.',
          detailedInfo: 'Excellent condition, barely worn, like new appearance'
        };
      case 'Good':
        return {
          label: 'Good Condition',
          badgeColor: 'bg-green-100 text-green-800 border-green-200',
          description: 'Used but remains in good condition.',
          detailedInfo: 'Normal wear for age, fully intact, ready to wear'
        };
      case 'Fair':
        return {
          label: 'Fair Condition',
          badgeColor: 'bg-orange-100 text-orange-800 border-orange-200',
          description: 'Noticeable signs of wear. Priced accordingly.',
          detailedInfo: 'Visible wear, character marks, discounted price'
        };
      case 'Brand New':
      default:
        return {
          label: 'Brand New',
          badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
          description: 'Never worn, in original condition. Perfect for you.',
          detailedInfo: 'Never worn, original condition, perfect'
        };
    }
  };

  const conditionInfo = getConditionInfo();
  const isPreLoved = product.condition === 'Pre-loved' || product.condition === 'Pre-loved (Used)';

  // Get the correct karat value
  const getKaratValue = (): string => {
    if (product.karat && product.karat.trim() !== '' && product.karat !== 'N/A') {
      return product.karat;
    }
    return 'N/A';
  };
  
  // Get the correct weight value
  const getWeightValue = (): string => {
    if (product.weight !== undefined && product.weight !== null) {
      if (typeof product.weight === 'number') {
        return product.weight.toFixed(2) + 'g';
      }
      if (typeof product.weight === 'string' && product.weight.trim() !== '' && product.weight !== 'N/A') {
        return product.weight;
      }
    }
    return 'N/A';
  };
  
  // Get the correct type/goldType value
  const getTypeValue = (): string => {
    if (product.goldType && product.goldType.trim() !== '' && product.goldType !== 'N/A') {
      return product.goldType;
    }
    
    const typeFromApi = extendedProduct.type;
    if (typeFromApi && typeof typeFromApi === 'string' && typeFromApi.trim() !== '' && typeFromApi !== 'N/A') {
      return typeFromApi;
    }
    
    return 'Standard';
  };
  
  // Format karat to display properly
  const formatKarat = (karat: string): string => {
    if (!karat || karat === 'N/A') return 'N/A';
    return karat;
  };
  
  // Format weight to display properly
  const formatWeight = (weight: string): string => {
    if (!weight || weight === 'N/A') return 'N/A';
    return weight;
  };
  
  // Format gold type
  const formatGoldType = (type: string): string => {
    if (!type || type === 'N/A') return 'Standard';
    return type;
  };
  
  const rawKarat = getKaratValue();
  const rawWeight = getWeightValue();
  const rawType = getTypeValue();
  
  const displayKarat = formatKarat(rawKarat);
  const displayWeight = formatWeight(rawWeight);
  const displayGoldType = formatGoldType(rawType);

  const totalPrice = displayPrice * detailQuantity;

  const currentImage = getCurrentImage();
  const hasImages = uniqueImages.length > 0;
  const hasMultipleImages = uniqueImages.length > 1;

  const isAnyProcessing = isAddToCartProcessing || isCheckoutProcessing;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-stone-900/80 backdrop-blur-md" 
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white w-full max-w-5xl h-[90vh] md:h-auto md:max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl flex flex-col md:flex-row animate-fade-up-enter fade-up-active mx-4 z-[301]">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 bg-white/80 rounded-full hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Left: Gallery */}
        <div className="w-full md:w-1/2 bg-stone-50 p-8 flex flex-col items-center justify-start relative">
          {hasImages ? (
            <>
              {/* Main Image */}
              <div className="relative w-full aspect-square max-w-md mb-4 group">
                <img 
                  src={currentImage} 
                  alt={product.name} 
                  className="w-full h-full object-cover shadow-sm transition-opacity duration-300" 
                  onError={handleMainImageError}
                />
                
                {/* Badge on Image */}
                {showPromo ? (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2">
                      <Clock size={16} /> {promoLabel}
                    </span>
                  </div>
                ) : isPreOrder ? (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-purple-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2">
                      <Clock size={16} /> PRE-ORDER
                    </span>
                  </div>
                ) : (
                  <div className="absolute top-4 left-4 z-10">
                    <span className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider shadow-md ${conditionInfo.badgeColor}`}>
                      {conditionInfo.label}
                    </span>
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {hasMultipleImages && (
                  <>
                    <button 
                      onClick={prevImage}
                      className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all ${
                        selectedImage === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
                      }`}
                      disabled={selectedImage === 0}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={nextImage}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all ${
                        selectedImage === uniqueImages.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
                      }`}
                      disabled={selectedImage === uniqueImages.length - 1}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {uniqueImages.length > 0 && (
                <div className="w-full max-w-md">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs uppercase tracking-widest text-stone-500 font-medium">
                      {hasMultipleImages ? 'MORE PHOTOS' : 'PRODUCT PHOTO'}
                    </h4>
                    <span className="text-[10px] text-stone-400 bg-stone-200/50 px-2 py-1 rounded-full">
                      {uniqueImages.length} {uniqueImages.length === 1 ? 'image' : 'images'}
                    </span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {uniqueImages.map((img, index) => (
                      <button 
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden transition-all ${
                          selectedImage === index 
                            ? 'ring-2 ring-gold-600 opacity-100' 
                            : 'ring-1 ring-stone-200 opacity-60 hover:opacity-100 hover:ring-gold-400'
                        }`}
                      >
                        <img 
                          src={img} 
                          alt={`${product.name} view ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(index)}
                        />
                      </button>
                    ))}
                  </div>
                  
                  {hasMultipleImages && (
                    <div className="text-center mt-3">
                      <span className="text-xs text-stone-400">
                        Viewing {selectedImage + 1} of {uniqueImages.length}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="w-full aspect-square max-w-md bg-stone-100 flex items-center justify-center mb-4 relative">
              <img src={PLACEHOLDER_IMAGE} alt="No image available" className="w-full h-full object-cover" />
              {showPromo ? (
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2">
                    <Clock size={16} /> {promoLabel}
                  </span>
                </div>
              ) : isPreOrder ? (
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-purple-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2">
                    <Clock size={16} /> PRE-ORDER
                  </span>
                </div>
              ) : (
                <div className="absolute top-4 left-4 z-10">
                  <span className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider shadow-md ${conditionInfo.badgeColor}`}>
                    {conditionInfo.label}
                  </span>
                </div>
              )}
            </div>
          )}

          {isCheckoutProcessing && (
            <button 
              onClick={() => {
                setIsCheckoutProcessing(false);
                setIsAddToCartProcessing(false);
                setStockError(null);
              }}
              className="mt-4 text-xs text-stone-400 hover:text-stone-600 underline"
            >
              Reset (stuck?)
            </button>
          )}
        </div>

        {/* Right: Details */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col">
          <div className="mb-auto">
            <div className="text-xs text-stone-400 uppercase tracking-widest mb-2 flex items-center gap-2 flex-wrap">
              <span className="font-medium">{product.category?.toUpperCase() || 'JEWELRY'}</span>
              <span className="w-1 h-1 bg-stone-300 rounded-full"></span>
              {showPromo ? (
                <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-sm flex items-center gap-1">
                  <Clock size={12} /> {promoLabel}
                </span>
              ) : isPreOrder ? (
                <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-sm flex items-center gap-1">
                  <Clock size={12} /> PRE-ORDER
                </span>
              ) : (
                <span className={`px-2 py-0.5 rounded-sm ${conditionInfo.badgeColor}`}>
                  {conditionInfo.label}
                </span>
              )}
            </div>
            
            <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-2">{product.name}</h2>
            
            {/* Stock Status */}
            <div className="mb-4">
              {isOutOfStock ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  <AlertCircle size={14} />
                  OUT OF STOCK
                </span>
              ) : stock > 0 && stock <= 5 ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <Clock size={14} />
                  Only {stock} left in stock
                </span>
              ) : null}
            </div>
            
            {/* Price Display */}
            <div className="flex items-center justify-between mb-6">
              <div>
                {showPromo ? (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-medium text-red-600">
                      ₱ {promoPrice?.toLocaleString() || '0'}
                    </span>
                    <span className="text-lg text-stone-400 line-through">
                      ₱ {product.price?.toLocaleString() || '0'}
                    </span>
                  </div>
                ) : (
                  <span className="text-2xl font-medium text-stone-900">
                    ₱ {product.price?.toLocaleString() || '0'}
                  </span>
                )}
              </div>
              {product.isBestSeller && (
                <span className="bg-gold-50 text-gold-700 text-[10px] uppercase font-bold px-3 py-1 border border-gold-200">
                  BEST SELLER
                </span>
              )}
            </div>

            {/* Stock Error Message */}
            {stockError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-red-600 text-xs flex items-center gap-2">
                <AlertCircle size={14} className="flex-shrink-0" />
                <span>{stockError}</span>
              </div>
            )}

            {/* PROMO HERO SECTION */}
            {showPromo && (
              <div className="mb-8 p-6 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-300 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <div className="bg-red-600 rounded-full p-3 flex-shrink-0">
                    <Clock size={28} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-red-800 mb-2 flex items-center gap-2">
                      {promoLabel} - SPECIAL OFFER
                      {promoPercentage && (
                        <span className="bg-red-600 text-white text-sm px-2 py-1 rounded-full">
                          {promoPercentage}% OFF
                        </span>
                      )}
                    </h3>
                    
                    <div className="space-y-2">
                      {savings > 0 && (
                        <p className="text-sm text-red-700">
                          You save: ₱ {savings.toLocaleString()} ({savingsPercentage}%)
                        </p>
                      )}
                      
                      {promoEndDate && (
                        <div className="flex items-center gap-2 text-sm text-red-700 bg-white/50 p-2 rounded">
                          <Calendar size={16} />
                          <span>Offer ends {formatDate(promoEndDate)}</span>
                        </div>
                      )}
                      
                      <p className="text-xs text-red-600 mt-2">
                        Limited time offer. Don't miss out.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PRE-ORDER HERO SECTION */}
            {!showPromo && isPreOrder && (
              <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-600 rounded-full p-3 flex-shrink-0">
                    <Clock size={28} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-purple-800 mb-2 flex items-center gap-2">
                      PRE-ORDER ITEM
                      {product.isBestSeller && (
                        <span className="bg-gold-500 text-white text-xs px-2 py-1 rounded-full">Bestseller</span>
                      )}
                    </h3>
                    <p className="text-purple-700 mb-3">
                      This item is available for pre-order. Your reservation secures this piece before its official release.
                    </p>
                    
                    <div className="bg-white/80 rounded-lg p-3 border border-purple-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar size={18} className="text-purple-600" />
                        <span className="font-medium text-purple-800">
                          {preOrderReleaseDate ? `Expected Release: ${formatReleaseDate(preOrderReleaseDate)}` : 'Release Date: Coming Soon'}
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-purple-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-purple-600">
                          A down payment may be required to reserve this item. You will be notified when it is ready to ship.
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="text-xs bg-white/60 p-2 rounded flex items-center gap-1">
                        <ShieldCheck size={14} className="text-purple-600" />
                        <span>Secure reservation</span>
                      </div>
                      <div className="text-xs bg-white/60 p-2 rounded flex items-center gap-1">
                        <Calendar size={14} className="text-purple-600" />
                        <span>Priority fulfillment</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Condition Details Section */}
            {!showPromo && !isPreOrder && (isPreLoved || conditionInfo.label !== 'Brand New') && (
              <div className={`mb-6 p-4 rounded-sm border ${conditionInfo.badgeColor.replace('text', 'border').replace('bg', 'bg-opacity-30')}`}>
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold uppercase mb-1">
                      {conditionInfo.label.toUpperCase()} ITEM
                    </h4>
                    <p className="text-sm mb-2">{conditionInfo.description}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <ShieldCheck size={14} />
                      <span>{conditionInfo.detailedInfo}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-b border-stone-100 py-6 mb-6">
              <p className="text-stone-600 font-light leading-relaxed mb-6">
                {product.description || 'Crafted with precision and care, this piece exemplifies the Sparkling Gems commitment to quality.'}
              </p>
              
              {/* Specs for jewelry category */}
              {(product.category === 'jewelry' || !product.category) && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-stone-50 rounded-sm">
                    <span className="block text-[10px] text-stone-400 uppercase tracking-wider mb-1">KARAT</span>
                    <span className="font-serif text-stone-900">{displayKarat}</span>
                  </div>
                  <div className="text-center p-3 bg-stone-50 rounded-sm">
                    <span className="block text-[10px] text-stone-400 uppercase tracking-wider mb-1">WEIGHT</span>
                    <span className="font-serif text-stone-900">{displayWeight}</span>
                  </div>
                  <div className="text-center p-3 bg-stone-50 rounded-sm">
                    <span className="block text-[10px] text-stone-400 uppercase tracking-wider mb-1">TYPE</span>
                    <span className="font-serif text-stone-900">{displayGoldType}</span>
                  </div>
                </div>
              )}
              
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4 p-3 bg-stone-100 text-xs text-stone-500 rounded-sm font-mono">
                  <div>Debug Info:</div>
                  <div>Karat: {rawKarat} | Weight: {rawWeight} | Type: {rawType}</div>
                  <div>goldType: {product.goldType || 'not set'}</div>
                  <div>type (API): {extendedProduct.type || 'not set'}</div>
                  <div>Stock: {stock}</div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase font-bold text-stone-500">QUANTITY</span>
                <div className="flex items-center border border-stone-200">
                  <button 
                    onClick={() => {
                      setDetailQuantity(Math.max(1, detailQuantity - 1));
                      setStockError(null);
                    }}
                    className="p-3 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                    disabled={isAnyProcessing || isOutOfStock}
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-12 text-center font-mono text-sm">{detailQuantity}</span>
                  <button 
                    onClick={() => {
                      setDetailQuantity(detailQuantity + 1);
                      setStockError(null);
                    }}
                    className="p-3 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                    disabled={isAnyProcessing || isOutOfStock || (stock > 0 && detailQuantity >= stock)}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {!isOutOfStock && stock > 0 && (
                  <span className="text-xs text-stone-400 ml-2">
                    Max: {stock}
                  </span>
                )}
              </div>

              <div className="flex gap-3">
                {/* WISHLIST BUTTON */}
                <button 
                  onClick={() => onToggleWishlist(product)}
                  disabled={isAnyProcessing}
                  className={`px-4 border transition-colors rounded-sm ${
                    isInWishlist(product.id) 
                      ? 'border-red-200 bg-red-50 text-red-500' 
                      : 'border-stone-200 text-stone-400 hover:border-stone-400 hover:text-stone-900'
                  }`}
                >
                  <Heart size={20} fill={isInWishlist(product.id) ? 'currentColor' : 'none'} />
                </button>
              </div>
              
              {/* PROCEED TO CHECKOUT BUTTON */}
              <button 
                onClick={handleCheckout}
                disabled={isAnyProcessing || isOutOfStock}
                className={`w-full py-4 uppercase text-xs tracking-[0.2em] transition-colors shadow-xl flex items-center justify-center gap-2 rounded-sm font-bold ${
                  isOutOfStock
                    ? 'bg-stone-300 cursor-not-allowed text-stone-600'
                    : showPromo 
                      ? 'bg-red-700 hover:bg-red-800 text-white' 
                      : isPreOrder 
                        ? 'bg-purple-700 hover:bg-purple-800 text-white' 
                        : 'bg-gold-600 hover:bg-gold-700 text-stone-900'
                }`}
              >
                {isOutOfStock ? (
                  <>
                    <AlertCircle size={16} /> 
                    UNAVAILABLE
                  </>
                ) : (
                  <>
                    <CreditCard size={16} /> 
                    {showPromo ? 'BUY NOW' : isPreOrder ? 'RESERVE NOW' : 'PROCEED TO CHECKOUT'} — ₱ {totalPrice.toLocaleString()}
                  </>
                )}
              </button>
              
              {/* Additional messaging */}
              {isPreOrder && !showPromo && (
                <p className="text-xs text-center text-purple-600 mt-2">
                  By placing a pre-order, you agree to our pre-order terms. No payment will be processed until the item ships.
                </p>
              )}
              
              {showPromo && (
                <p className="text-xs text-center text-red-600 mt-2">
                  Limited time offer. Price as marked.
                </p>
              )}
            </div>
          </div>    

          <div className="mt-8 pt-6 border-t border-stone-100 grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Truck size={18} className="text-gold-600 mt-1" />
              <div>
                <h4 className="text-xs font-bold uppercase text-stone-900">SHIPPING</h4>
                <p className="text-[10px] text-stone-500 leading-tight mt-1">Insured delivery within Philippines.</p>
                {isPreOrder && !showPromo && (
                  <p className="text-[9px] text-purple-600 mt-1">Ships after release date</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="text-gold-600 mt-1" />
              <div>
                <h4 className="text-xs font-bold uppercase text-stone-900">AUTHENTICITY</h4>
                <p className="text-[10px] text-stone-500 leading-tight mt-1">Certified genuine and verified.</p>
                {isPreLoved && !showPromo && (
                  <p className="text-[9px] text-amber-600 mt-1">Pre-owned, fully authenticated</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;